import API from '../api/api';

// Theme settings store images as `shopify://shop_images/<filename>`. The Files search resolves
// such a reference to its CDN URL; the result is cached per reference for the session.
const REF = /^shopify:\/\/shop_images\/(.+)$/i;
// file reference metafields and metaobject fields store the file's gid instead
const MEDIA_GID = /^gid:\/\/shopify\/MediaImage\/\d+$/;
const cache = {};    // ref -> url, or null when no file matched
const gids = {};     // ref -> the file's Shopify id, for the admin link
const waiting = {};  // ref -> callbacks queued while a lookup runs

export const isShopImageRef = (value) => typeof value === 'string' && REF.test(value.trim());
export const isMediaImageGid = (value) => typeof value === 'string' && MEDIA_GID.test(value.trim());

// a text field whose source is an image URL or a shop image reference is shown as an image row
const IMAGE_URL = /^(https?:)?\/\/\S+\.(png|jpe?g|gif|webp|svg|avif)(\?\S*)?$/i;
export const isImageSource = (value) => typeof value === 'string' && (IMAGE_URL.test(value.trim()) || isShopImageRef(value) || isMediaImageGid(value));

export const shopImageFilename = (ref) => {
  const match = String(ref).trim().match(REF);
  return match ? decodeURIComponent(match[1]) : '';
};

const filenameOf = (url) => {
  try {
    return decodeURIComponent(new URL(url).pathname.split('/').pop());
  } catch {
    return String(url).split('/').pop().split('?')[0];
  }
};

// the reference for a file the shop already serves from its CDN
export const shopImageRef = (url) => `shopify://shop_images/${filenameOf(url)}`;

// a reference built from a known URL needs no lookup
export const rememberShopImage = (ref, url, gid = null) => { cache[ref] = url; if(gid) gids[ref] = gid; };

// the file's gid once a lookup or a library pick delivered it, else null
export const shopImageGid = (ref) => gids[ref] || null;

export const rememberMediaImage = (gid, url) => { cache[gid] = url; };

// a media image gid resolves through the same Files search: the backend turns a gid into an id
// lookup. Cached and queued like the references above
export const resolveMediaImage = (gid, callback) => {
  if(cache[gid] !== undefined) { callback(cache[gid]); return; }
  if(waiting[gid]) { waiting[gid].push(callback); return; }
  waiting[gid] = [callback];

  API.searchMore('images', gid, null, res => {
    const results = res?.results?.images?.results || [];
    const match = results.find(f => f.gid === gid) || (results.length === 1 ? results[0] : null);
    cache[gid] = match ? match.title : null;
    const callbacks = waiting[gid];
    delete waiting[gid];
    callbacks.forEach(cb => cb(cache[gid]));
  });
};

export const resolveShopImage = (ref, callback) => {
  if(cache[ref] !== undefined) { callback(cache[ref]); return; }
  if(waiting[ref]) { waiting[ref].push(callback); return; }
  waiting[ref] = [callback];

  const filename = shopImageFilename(ref);
  API.searchMore('images', filename, null, res => {
    const results = res?.results?.images?.results || [];
    // the exact filename, or the only hit; Shopify suffixes duplicate names, so anything
    // looser could pick a different file
    const exact = results.find(f => filenameOf(f.title).toLowerCase() === filename.toLowerCase());
    const match = exact || (results.length === 1 ? results[0] : null);
    cache[ref] = match ? match.title : null;
    if(match && match.gid) gids[ref] = match.gid;
    const callbacks = waiting[ref];
    delete waiting[ref];
    callbacks.forEach(cb => cb(cache[ref]));
  });
};
